import { useRouter } from "next/router";
import { useState } from "react";

import { Button, Card, Skeleton } from "@chef/components";
import { BRAND_PATHS, FEATURE_FLAGS } from "@chef/constants";
import {
  useBillingQuery,
  useUpdateAddressMutation,
} from "@chef/state-management";
import { AppShell } from "@chef/feature-app-shell";
import { isMealboxBasketType } from "@chef/helpers";
import { Modal } from "@chef/smart-components";
import { Warning } from "@chef/icons/symbol";
import { padPostalCode } from "@chef/helpers";
import { useFlag } from "@chef/feature-flags";

import { MyAccountLayout } from "../../components/MyAccountLayout";
import { AddressForm, AddressFormValues } from "../../components/AddressForm";

import { intl } from "./MyAccountEditAddressPage.Intl";

export const MyAccountEditAddressPage = () => {
  const router = useRouter();
  const addressId = router.query["addressId"] as string;

  const { enabled: isRemoveAddressesEnabled } = useFlag(
    FEATURE_FLAGS.REMOVE_ADDRESSES,
  );

  const [isRemoveAddressModalOpen, setIsRemoveAddressModalOpen] =
    useState(false);

  const { data: billingQuery, isLoading } = useBillingQuery();

  const activeAddress = billingQuery?.billing.addresses?.find(
    (address) => address.addressId === addressId,
  );

  const [updateAddressMutation, { isLoading: isUpdatingAddress }] =
    useUpdateAddressMutation();

  const updateAddress = async (formValues: AddressFormValues) => {
    try {
      await updateAddressMutation({
        addressId,
        postalArea: formValues.postalArea,
        streetAddress: formValues.streetAddress,
        postalCode: formValues.postalCode,
        instructions:
          formValues.instructionsToDriver !== null
            ? formValues.instructionsToDriver
            : undefined,
        alias: formValues.name || formValues.streetAddress,
        doorCode:
          formValues.doorCode !== null ? formValues.doorCode : undefined,
        requiresPhysicalKey: formValues.requiresPhysicalKey || undefined,
        apartmentName: formValues.apartment.name,
        floor: formValues.apartment.floor,
        flatNo: formValues.apartment.door || undefined,
        addressUUID: formValues.validatedAddress.addressGuid,
        addressCode: formValues.validatedAddress.addressCode,
        latitude: formValues.validatedAddress.latitude,
        longitude: formValues.validatedAddress.longitude,
        streetNumber: formValues.validatedAddress.streetNumber
          ? +formValues.validatedAddress.streetNumber
          : undefined,
        letterExtra: formValues.validatedAddress.letterExtra,
        streetName: formValues.validatedAddress.streetName,
        addressName: formValues.validatedAddress.addressName,
        addressDigitalKeyProviderId:
          formValues.validatedAddress.digitalKeyProvider
            ?.addressDigitalKeyProviderId,
        validated: activeAddress?.validated,
      }).unwrap();

      router.push(BRAND_PATHS.DELIVERY_INFORMATION_PAGE);
    } catch (err) {
      console.error(err);
    }
  };

  const mealboxBasket = billingQuery?.billing.baskets.find(isMealboxBasketType);
  const triggerModal = () => {
    setIsRemoveAddressModalOpen((prev) => !prev);
  };

  const deleteAddress = async () => {
    if (!activeAddress) {
      return;
    }
    try {
      await updateAddressMutation({
        addressId,
        streetAddress: activeAddress.address,
        postalCode: padPostalCode(activeAddress.postalCode.toString()),
        postalArea: activeAddress.city,
        alias: activeAddress.name,
        isVisible: false,
      }).unwrap();

      triggerModal();
      router.push(BRAND_PATHS.DELIVERY_INFORMATION_PAGE);
    } catch (err) {
      console.error(err);
    }
  };

  const defaultBasket = billingQuery?.billing.baskets.find(isMealboxBasketType);

  const activeAddressIsDeliveryAddress =
    activeAddress?.addressId === mealboxBasket?.shippingAddressId;

  return (
    <AppShell title={intl.EDIT_ADDRESS}>
      <AppShell.Meta />
      <AppShell.Header />
      <AppShell.AccountPausedBanner />
      <AppShell.OldVippsBanner />

      <AppShell.Main grid>
        <MyAccountLayout
          page="deliveryInformation"
          title={intl.EDIT_ADDRESS}
          hideBackOnMobile
        >
          <div className="-mx-4 -mt-10 md:m-0 col-span-full md:col-span-5">
            <Card>
              {isLoading && <Preloader />}

              {activeAddress && billingQuery?.billing && (
                <AddressForm
                  onFormSubmitted={updateAddress}
                  initialAddress={activeAddress}
                  isLoading={isUpdatingAddress}
                  {...(activeAddressIsDeliveryAddress && {
                    initialTimeblockId: defaultBasket?.timeblockId,
                    initialBasketDeliveryWeekType:
                      defaultBasket?.deliveryInterval,
                  })}
                />
              )}
            </Card>
            {!activeAddressIsDeliveryAddress && isRemoveAddressesEnabled && (
              <div className="flex justify-center mt-12 underline text-error">
                <button
                  id="open-remove-address-modal-button"
                  onClick={triggerModal}
                >
                  {intl.REMOVE_ADDRESS}
                </button>
              </div>
            )}
          </div>
        </MyAccountLayout>
        <Modal open={isRemoveAddressModalOpen} onClose={triggerModal} size="md">
          <Card noPadding>
            <div className="px-6 pb-6 pt-22 lg:pt-18 lg:px-22 lg:pb-20">
              <div className="max-w-md mx-auto mb-8 text-center ">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-grey-3">
                  <Warning className="w-16 h-16 my-1" />
                </div>
                <h2>
                  <strong>
                    {intl.ARE_YOU_SURE_YOU_WANT_TO_REMOVE_ADDRESS}
                  </strong>
                </h2>
                <p className="mt-2 text-sm">
                  {intl.THE_ACTION_CANNOT_BE_UNDONE}
                </p>
              </div>
              <div className="flex flex-col max-w-xs mx-auto">
                <Button
                  id="remove-address-modal-button"
                  className="mb-4"
                  primary
                  onClick={deleteAddress}
                >
                  {intl.REMOVE_ADDRESS}
                </Button>
                <Button
                  id="cancel-remove-address-modal-button"
                  outlined
                  onClick={triggerModal}
                >
                  {intl.CANCEL}
                </Button>
              </div>
            </div>
          </Card>
        </Modal>
      </AppShell.Main>
      <AppShell.TabBar />
    </AppShell>
  );
};

const Preloader = () => {
  return (
    <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
      <Skeleton height={8} className="col-span-1" />

      <Skeleton height={12} className="col-span-full" />

      <Skeleton height={12} className="col-span-1" />
      <Skeleton height={12} className="col-span-1 md:col-span-3" />

      <Skeleton height={12} className="col-span-full" />
      <Skeleton height={12} className="col-span-full" />
      <Skeleton height={12} className="col-span-full" />
    </div>
  );
};
